/**
 * Five core ability papers shown on the ability test grid.
 * Full question content is added per paper over time; placeholders use comingSoon.
 */
import { criticalThinkingTestPaper } from './criticalThinkingTestPaper.js';
import { quantitativeReasoningTestPaper } from './quantitativeReasoningTestPaper.js';
import { logicalReasoningTestPaper } from './logicalReasoningTestPaper.js';
import { verbalReasoningTestPaper } from './verbalReasoningTestPaper.js';
import { communicationTestPaper } from './communicationTestPaper.js';

const L = (en, tc, sc) => ({
  en,
  tc: tc != null ? tc : en,
  sc: sc != null ? sc : en
});

/** Five core ability papers — sole canonical slug list; keep aligned with config/productVectors.js */
export const CORE_ABILITY_SLUGS = [
  'quantitative-reasoning',
  'logical-reasoning',
  'critical-thinking',
  'verbal-reasoning',
  'communication'
];

export const CORE_ABILITY_COUNT = CORE_ABILITY_SLUGS.length;

/** @type {Record<string, string>} */
export const ABILITY_SLUG_ALIASES = {
  // keep legacy alias for old DB docs / links
  'critical-thinking-test': 'critical-thinking'
};

/**
 * @param {string} slug
 */
export function normalizeAbilitySlug(slug) {
  return ABILITY_SLUG_ALIASES[slug] || slug;
}

/**
 * @param {object} opts
 */
function placeholderAbilityPaper({
  slug,
  titleEn,
  titleTc,
  titleSc,
  order,
  infographic
}) {
  return {
    slug,
    title: L(titleEn, titleTc, titleSc),
    paperInstruction: L(
      'This ability assessment is being prepared. Check back soon to complete it and add to your profile.',
      '此能力評估正在準備中，請稍後再回來完成，以豐富你的個人檔案。',
      '此能力评估正在准备中，请稍后再回来完成，以丰富你的个人档案。'
    ),
    timeLimitSeconds: 3600,
    order,
    isPublished: true,
    comingSoon: true,
    infographic,
    sections: []
  };
}

const logicalReasoningCore = {
  ...logicalReasoningTestPaper,
  slug: 'logical-reasoning',
  order: 0,
  comingSoon: false,
  infographic: 'logical-reasoning'
};

const criticalThinkingCore = {
  ...criticalThinkingTestPaper,
  slug: 'critical-thinking',
  title: L('Critical Thinking', '批判性思維', '批判性思维'),
  order: 1,
  comingSoon: false,
  infographic: 'critical-thinking'
};

const verbalReasoningCore = {
  ...verbalReasoningTestPaper,
  slug: 'verbal-reasoning',
  order: 2,
  comingSoon: false,
  infographic: 'verbal-reasoning'
};

const communicationCore = {
  ...communicationTestPaper,
  slug: 'communication',
  order: 3,
  comingSoon: false,
  infographic: 'communication'
};

const quantitativeReasoningCore = {
  ...quantitativeReasoningTestPaper,
  slug: 'quantitative-reasoning',
  order: 4,
  comingSoon: false,
  infographic: 'quantitative-reasoning'
};

/**
 * Canonical five ability papers in play / grid order.
 * Quantitative is last to lower the mental cost of starting the suite.
 * Vector indices stay in CORE_ABILITY_SLUGS / config/productVectors.js.
 */
export function getCoreAbilityPapers() {
  return [
    logicalReasoningCore,
    criticalThinkingCore,
    verbalReasoningCore,
    communicationCore,
    quantitativeReasoningCore
  ];
}

/**
 * Merge API papers into the fixed five-tile grid (API content wins when slug matches).
 * @param {object[]} apiPapers
 */
export function mergeAbilityPapersWithApi(apiPapers) {
  const bySlug = new Map();
  for (const paper of apiPapers || []) {
    bySlug.set(normalizeAbilitySlug(paper.slug), paper);
  }
  return getCoreAbilityPapers().map((core) => {
    const fromApi = bySlug.get(core.slug);
    if (!fromApi) return core;
    const apiHasSections = Array.isArray(fromApi.sections) && fromApi.sections.length > 0;
    const coreHasSections = Array.isArray(core.sections) && core.sections.length > 0;
    return {
      ...core,
      ...fromApi,
      slug: core.slug,
      infographic: core.infographic,
      comingSoon: fromApi.comingSoon === true || (!apiHasSections && !coreHasSections),
      title: fromApi.title || core.title,
      order: core.order,
      sections: apiHasSections ? fromApi.sections : core.sections
    };
  });
}

/**
 * Papers that are live and contain at least one question.
 * @param {object[]} papers
 */
export function getPlayableAbilityPapers(papers) {
  return (papers || []).filter((paper) => {
    if (!paper || paper.comingSoon) return false;
    const sections = paper.sections || [];
    return sections.some((section) => (section.questions || []).length > 0);
  });
}

/**
 * True when every core ability paper has a submitted (or timed-out) attempt on record.
 * @param {Set<string> | null | undefined} completedSlugs
 */
export function areAllCoreAbilitiesComplete(completedSlugs) {
  if (!(completedSlugs instanceof Set)) return false;
  return CORE_ABILITY_SLUGS.every((slug) => completedSlugs.has(slug));
}

/**
 * @param {object[]} papers
 * @param {object | null | undefined} paper
 */
export function getPlayablePaperIndex(papers, paper) {
  if (!paper) return -1;
  const slug = normalizeAbilitySlug(paper.slug);
  return getPlayableAbilityPapers(papers).findIndex(
    (entry) => normalizeAbilitySlug(entry.slug) === slug
  );
}

/**
 * @param {{ paperSlug?: string, completionReason?: string, attemptId?: string }[]} submissions
 * @param {string | null} [attemptId] — when set, only submissions for this suite run count
 * @returns {Set<string>}
 */
export function getCompletedAbilitySlugs(submissions, attemptId = null) {
  const slugs = new Set();
  for (const row of submissions || []) {
    if (!row.paperSlug) continue;
    if (attemptId) {
      if (row.attemptId !== attemptId) continue;
    } else if (row.attemptId) {
      continue;
    }
    if (row.completionReason === 'submitted' || row.completionReason === 'timed_out') {
      slugs.add(normalizeAbilitySlug(row.paperSlug));
    }
  }
  return slugs;
}
